import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Download, ExternalLink, Lock, ShieldAlert, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { Badge, Btn, Card, CardHead, PageHeader } from '../../components/cld';
import { GoogleAdsNoLinkCard, GoogleAdsNoWatchedCard } from '../../components/cld/google-ads-empty';
import { PoolDetector } from '../../components/cld/pool-detector';
import { api } from '../../lib/api';
import { useSiteAdAccounts } from '../../lib/use-site-ad-accounts';
import { filterAuditToWatched, useWatchedSet } from '../../lib/watched-audit';

const NEG_KEYWORDS_URL = 'https://ads.google.com/aw/keywords/negative';

export function GoogleAdsActionPage(): ReactElement {
  const { t } = useTranslation('pages');
  const nf = new Intl.NumberFormat('fr-FR');
  const money = (eur: number) => `${nf.format(Math.round(eur))} €`;

  const { projectId, connected, accounts, loading } = useSiteAdAccounts();
  const [picked, setPicked] = useState<string | null>(null);
  const cid = picked ?? accounts[0]?.id ?? null;
  const { watchedIds } = useWatchedSet(projectId, cid);

  const auditQ = useQuery({
    queryKey: ['gads-audit', cid],
    queryFn: () => api.googleAdsAudit(cid as string),
    enabled: !!cid,
  });
  const termsQ = useQuery({
    queryKey: ['gads-terms', cid],
    queryFn: () => api.googleAdsSearchTerms(cid as string),
    enabled: !!cid,
  });

  const [prepared, setPrepared] = useState<string[]>([]);
  const [seen, setSeen] = useState(false);

  const audit =
    auditQ.data && watchedIds ? filterAuditToWatched(auditQ.data, watchedIds) : auditQ.data;
  const risky = (audit?.campaigns ?? []).filter((c) => c.wasteScore >= 10);
  const terms = termsQ.data?.terms ?? [];
  const showCaveat = !!termsQ.data && !termsQ.data.siloConversionAvailable;
  const caveatText = termsQ.data?.orgHasTracker
    ? t('gadsa.caveatTracker')
    : t('gadsa.caveatNoTracker');

  const header = (
    <PageHeader
      title={t('gadsa.title')}
      sub={t('gadsa.subtitle')}
      actions={
        connected && accounts.length > 0 ? (
          <div className="flex items-center gap-2">
            <select
              value={cid ?? ''}
              onChange={(e) => setPicked(e.target.value)}
              className="rounded-[9px] border border-border-strong bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ? `${a.name} (${a.id})` : a.id}
                </option>
              ))}
            </select>
            <Btn
              icon={Download}
              onClick={() => {
                const head = 'type,label,cost_eur,detail';
                const rows = [
                  ...risky.map(
                    (c) =>
                      `campaign,"${c.name}",${Math.round(c.wastedEur)},"waste ${c.wasteScore}"`,
                  ),
                  ...terms.map(
                    (s) =>
                      `search_term,"${s.term}",${Math.round(s.costEur)},"${s.clicks} clicks · ${s.action}"`,
                  ),
                ];
                const blob = new Blob([`${head}\n${rows.join('\n')}`], {
                  type: 'text/csv;charset=utf-8',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `silospot-action-center-${cid}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              {t('gadsa.export')}
            </Btn>
            <Btn icon={Check} onClick={() => setSeen(true)}>
              {seen ? t('gadsa.seen') : t('gadsa.markAllSeen')}
            </Btn>
          </div>
        ) : undefined
      }
    />
  );

  if (!connected) {
    return (
      <div>
        {header}
        {projectId ? <PoolDetector projectId={projectId} /> : null}
        <Card className="mx-auto max-w-md py-10 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
            <ShieldAlert className="h-6 w-6" />
          </span>
          <div className="text-[16px] font-bold text-text">{t('gadsp.notConnectedTitle')}</div>
          <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">
            {t('gadsp.notConnectedBody')}
          </p>
        </Card>
      </div>
    );
  }

  if (connected && !loading && accounts.length === 0) {
    return (
      <div>
        {header}
        {projectId ? <PoolDetector projectId={projectId} /> : null}
        <GoogleAdsNoLinkCard projectId={projectId} />
      </div>
    );
  }

  if (connected && !loading && accounts.length > 0 && watchedIds && watchedIds.size === 0) {
    return (
      <div>
        {header}
        {projectId ? <PoolDetector projectId={projectId} /> : null}
        <GoogleAdsNoWatchedCard projectId={projectId} />
      </div>
    );
  }

  const addTerm = (term: string) => {
    if (!prepared.includes(term)) setPrepared((p) => [...p, term]);
    toast.success(t('gadsa.exclude'));
  };
  const exclusionCsv = () => {
    const blob = new Blob([prepared.map((x) => `"${x}"`).join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `silospot-negative-keywords-${cid}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={seen ? 'opacity-95' : ''}>
      {header}
      {projectId ? <PoolDetector projectId={projectId} /> : null}

      {/* Campaigns at risk */}
      <Card className="p-0">
        <div className="p-4 pb-2">
          <CardHead title={t('gadsa.riskyTitle')} sub={t('gadsa.riskySub')} />
        </div>
        {auditQ.isLoading ? (
          <div className="h-32 animate-pulse bg-neutral-bg" />
        ) : risky.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-text-3">{t('gadsa.noRisky')}</p>
        ) : (
          <div className="flex flex-col">
            {risky.map((c) => (
              <div
                key={c.campaignId}
                className="flex flex-wrap items-center gap-3 border-b border-[#f1f3f6] px-4 py-3 last:border-0"
              >
                <Badge
                  tone={c.wasteScore >= 66 ? 'fraud' : c.wasteScore >= 33 ? 'suspect' : 'clean'}
                >
                  {c.wasteScore}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-text">{c.name}</div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {c.reasons.slice(0, 3).map((r) => (
                      <span
                        key={r.code}
                        className="rounded border border-[#f3c9c9] bg-fraud-bg px-1.5 py-0.5 text-[11px] text-fraud-fg"
                      >
                        {t(`gadsr.${r.code}`)}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="font-mono text-[12.5px] text-[#b45309]">{money(c.wastedEur)}</span>
                <Btn disabled title={t('gadsa.claimsSoon')}>
                  {t('gadsa.prepareClaim')}
                </Btn>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Suspicious search terms */}
      <Card className="mt-4 p-0">
        <div className="p-4 pb-2">
          <CardHead title={t('gadsa.termsTitle')} sub={t('gadsa.termsSub')} />
        </div>
        {showCaveat ? (
          <div className="mx-4 mb-2 flex items-start gap-2 rounded-lg border border-[#f6e2c4] bg-[#fdf4e7] px-3.5 py-2.5 text-[12px] text-suspect-fg">
            <span>⚠</span>
            <span>{caveatText}</span>
          </div>
        ) : null}
        {termsQ.isLoading ? (
          <div className="h-32 animate-pulse bg-neutral-bg" />
        ) : terms.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-text-3">{t('gadsa.noTerms')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('gadsa.colTerm'),
                    t('gadsa.colCampaign'),
                    t('gadsa.colClicks'),
                    t('gadsa.colCost'),
                    '',
                  ].map((h, i) => (
                    <th
                      key={h || i}
                      className={`border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase text-text-3 ${i >= 2 && i <= 3 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {terms.map((s) => (
                  <tr key={`${s.term}-${s.campaign}`} className="hover:bg-[#fafbfc]">
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5">
                      <span className="font-medium text-text">{s.term}</span>
                      {s.reason !== 'review' ? (
                        <span className="ml-2 rounded border border-[#f3c9c9] bg-fraud-bg px-1.5 py-0.5 text-[10.5px] text-fraud-fg">
                          {t(s.reason === 'junk' ? 'gadsa.reasonJunk' : 'gadsa.reasonFraud')}
                        </span>
                      ) : null}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-[12px] text-text-3">
                      {s.campaign}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
                      {nf.format(s.clicks)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-[#b45309]">
                      {money(s.costEur)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right">
                      {s.action === 'exclude' ? (
                        <button
                          type="button"
                          onClick={() => addTerm(s.term)}
                          disabled={prepared.includes(s.term)}
                          className="text-[12.5px] font-semibold text-accent-strong hover:underline disabled:opacity-40"
                        >
                          {t('gadsa.exclude')}
                        </button>
                      ) : (
                        <span className="rounded-md bg-neutral-bg px-2 py-0.5 text-[12px] text-text-3">
                          {t('gadsa.review')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Prepared exclusion list */}
      <Card className="mt-4">
        <CardHead
          title={t('gadsa.preparedTitle')}
          sub={t('gadsa.preparedSub')}
          right={
            prepared.length > 0 ? (
              <div className="flex items-center gap-2">
                <Btn
                  icon={Copy}
                  onClick={() => {
                    void navigator.clipboard.writeText(prepared.join('\n'));
                    toast.success(t('gadsa.copied'));
                  }}
                >
                  {t('gadsa.copy')}
                </Btn>
                <Btn icon={Download} onClick={exclusionCsv}>
                  {t('gadsa.exportList')}
                </Btn>
                <a
                  href={NEG_KEYWORDS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[9px] border border-border-strong px-3 py-1.5 text-[13px] font-semibold text-text-2 hover:bg-bg"
                >
                  {t('gadsa.openGoogleAds')} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            ) : undefined
          }
        />
        {prepared.length === 0 ? (
          <p className="py-3 text-[13px] text-text-3">{t('gadsa.preparedEmpty')}</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {prepared.map((x) => (
              <span
                key={x}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg px-2.5 py-1 text-[12.5px]"
              >
                {x}
                <button
                  type="button"
                  aria-label={t('gadsa.remove')}
                  onClick={() => setPrepared((p) => p.filter((y) => y !== x))}
                >
                  <X className="h-3.5 w-3.5 text-text-3 hover:text-fraud-fg" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Cloudflare-gated automatic actions */}
      <Card className="mt-4 opacity-90">
        <CardHead title={t('gadsa.autoTitle')} />
        <div className="flex items-start gap-2.5 text-[13px] text-text-3">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{t('gadsa.autoLocked')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="cursor-not-allowed rounded-[9px] border border-border bg-neutral-bg px-3 py-1.5 text-[12.5px] text-text-3">
                {t('gadsa.blockIp')}
              </span>
              <span className="cursor-not-allowed rounded-[9px] border border-border bg-neutral-bg px-3 py-1.5 text-[12.5px] text-text-3">
                {t('gadsa.activateRule')}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-[11px] py-[7px] text-[11.5px] leading-snug text-suspect-fg">
        {t('gadsa.estimatedNote')}
      </div>
    </div>
  );
}
